import { useAsyncDebounce } from '@/features/shared/hooks/useAsyncDebounce'
import {
  addProductsToCart,
  convertCart as convertCartData,
  mergeCart,
  transferCart,
  transferProducts,
  updateCart,
} from '@/features/shop/services/Cart/fetchers'
import { type MutationProduct } from '@shared/types'
import {
  applyProductsToCartLookups,
  createDefaultCartQueryState,
  getOptimisticCartQuantity,
  quantityForProductType,
} from '@/features/shop/services/Cart/utils'
import { type Cart } from '@/features/shop/services/Cart/types'
import { refreshCoupons } from '@/features/shop/services/Coupons/utils'
import { useCompleteGuestUserSignup } from '@/features/account/services/User/mutations'
import { getStoreParams } from '@/features/shared/utils/dataFetching/storeParamsUtils'
import { useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { logError, logInfo } from '@/features/shared/utils/logger'
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCartOnSuccess } from '@/features/shop/services/Cart/hooks'
import {
  CartQuery,
  cartQueryOptions,
  type CartQueryKey,
} from '@/features/shop/services/Cart/queries'
import { AllCartsPrimaryKey } from '@/features/shop/services/Carts/queries'
import { useGuestParams } from '@/features/account/services/GuestUser/hooks'
import { type User } from '@/features/account/services/User/types'
import { useGlobalDefaultShoppingAddress } from '@/features/account/services/Addresses/hooks'
import { datadogRum } from '@datadog/browser-rum'
import { fetchGraphql } from '@/features/shared/utils/dataFetching/reactQuery/graphql'
import { CartEmptyDocument, type ProductDataInput } from '@/gql/graphql'
import { useCartGraphQLInput } from '@/features/shop/services/Cart/useCartGraphQLInput'
import { useLazyInitializedRef } from '@/features/shared/hooks/useLazyInitializedRef'
import { type GuestStore } from '@/features/shop/services/ShoppingStore/types'
import { type Address } from '@/features/account/services/Addresses/types'
import { clearGuestCookies } from '@/features/account/services/GuestUser/utils'
import { invalidateFeaturedPromotionQueries } from '@/features/shop/components/FeaturedPromotionContainer/fetchers'
import { noop } from '@/features/shared/utils/noop'

export const invalidateCartQueries = async ({
  queryClient,
  queryKey,
}: {
  queryClient: QueryClient
  queryKey: CartQueryKey
}) => {
  invalidateFeaturedPromotionQueries(queryClient)
  queryClient.invalidateQueries({ queryKey: [AllCartsPrimaryKey] })
  /* accurate cart id is needed for our "Product Added" analytics event,
  so we need to be able to await the refetch of our cart query */
  await queryClient.invalidateQueries({ queryKey })
}

const CART_UPDATE_KEY = ['cartUpdate']

// will be true while more than one serialized cart update is still in flight
const hasPendingCartUpdates = (queryClient: QueryClient) =>
  queryClient.isMutating({ mutationKey: CART_UPDATE_KEY }) > 1

const getOptimisticCartUpdate = (cart: Cart, product: MutationProduct): Cart => {
  const lines = cart.shopping_cart_lines
  const oosLines = cart.shopping_cart_lines_oos
  const isRemoval = product.quantity === 0

  const matchesProduct = (line: { id: number; name: string }) =>
    line.id === product.id && line.name.toLowerCase() === product.name.toLowerCase()

  return {
    ...cart,
    shopping_cart_lines: isRemoval
      ? lines.filter((line) => !matchesProduct(line))
      : lines.map((line) =>
          matchesProduct(line) ? { ...line, note: product.note, quantity: product.quantity } : line,
        ),
    shopping_cart_lines_oos: isRemoval
      ? oosLines.filter((line) => line.id !== product.id)
      : oosLines,
    num_items: getOptimisticCartQuantity(lines, [product]),
    ...applyProductsToCartLookups(cart, [product]),
  }
}

export const useMutationAddToCart = () => {
  const storeParams = useStoreParams()
  const cartOnSuccess = useCartOnSuccess()
  const state = useGlobalDefaultShoppingAddress().state ?? ''
  const queryKey = cartQueryOptions({ storeParams, state }).queryKey

  return useMutation({
    mutationFn: async (products: MutationProduct[]) =>
      addProductsToCart({
        state,
        storeParams,
        products,
        fetcherName: 'useMutationAddToCartBff',
      }),
    onMutate: async (products, context) => {
      await context.client.cancelQueries({ queryKey })

      const previousCart = context.client.getQueryData(queryKey)

      // Optimistically update num_items and quantity/note maps.
      // We do NOT push products into shopping_cart_lines because:
      // 1. the product shape differs between different services
      // 2. the final cart data arrives via invalidateCartQueries in onSettled
      const baseCart = previousCart ?? createDefaultCartQueryState()

      context.client.setQueryData(queryKey, {
        ...baseCart,
        num_items: getOptimisticCartQuantity(baseCart.shopping_cart_lines, products),
        ...applyProductsToCartLookups(baseCart, products),
      })

      // Return a context object with the snapshotted value
      return { previousCart }
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (_error, _variables, onMutateResult, context) => {
      if (onMutateResult?.previousCart) {
        context.client.setQueryData(
          cartQueryOptions({ storeParams, state }).queryKey,
          onMutateResult.previousCart,
        )
      }
    },
    onSuccess: (cart, products, context) => {
      if (products[0]) {
        return cartOnSuccess(cart, products[0], context.previousCart)
      }
    },
    onSettled: async (_data, error, products, _, context) => {
      datadogRum.addAction('addToCart', {
        isSuccess: !error,
        productCount: products.length,
        mutationType: 'add',
      })
      /* accurate cart id is needed for our "Product Added" analytics event,
      so we need to await the refetch of our cart query here */
      await invalidateCartQueries({ queryClient: context.client, queryKey })
    },
  })
}

export const useMutationUpdateCart = ({
  serializeUpdates = false,
}: { serializeUpdates?: boolean } = {}) => {
  const storeParams = useStoreParams()
  const debouncedUpdateCart = useAsyncDebounce(updateCart, 500)
  const cartOnSuccess = useCartOnSuccess()
  const cartInput = useCartGraphQLInput()
  const { queryKey } = cartQueryOptions({ storeParams, state: cartInput.state })

  return useMutation({
    // On the cart page, the updates always run one-at-a-time to keep user rapid deletes from racing each other
    scope: serializeUpdates ? { id: 'cartUpdate' } : undefined,
    mutationKey: serializeUpdates ? CART_UPDATE_KEY : undefined,
    mutationFn: (product: MutationProduct) => {
      const { id, name, note, isCustomProduct, description } = product

      const productDataInput: ProductDataInput = {
        // custom products don't have a product ID,
        // so we send empty string as the ID and rely on description field.
        productId: isCustomProduct ? '' : String(id),
        description: isCustomProduct ? name : description,
        quantity: quantityForProductType(product),
        notes: note,
      }
      return serializeUpdates
        ? updateCart(cartInput, productDataInput)
        : debouncedUpdateCart(cartInput, productDataInput)
    },
    onMutate: async (product, context) => {
      await context.client.cancelQueries({ queryKey })

      const previousCart = context.client.getQueryData(queryKey)

      if (previousCart) {
        context.client.setQueryData(queryKey, getOptimisticCartUpdate(previousCart, product))
      }

      return { previousCart }
    },
    onError: (_error, _variables, onMutateResult, context) => {
      // Roll back to previous cart on error
      if (onMutateResult?.previousCart) {
        context.client.setQueryData(queryKey, onMutateResult.previousCart)
      }
    },
    onSuccess: (response, product, context) => {
      cartOnSuccess(response, product, context.previousCart)
    },
    onSettled: async (_data, error, _product, _, context) => {
      datadogRum.addAction('addToCart', {
        isGQL: true,
        isSuccess: !error,
        productCount: 1,
        mutationType: 'update',
      })
      // While updates are serialized, only the last in-flight one refetches.
      if (serializeUpdates && hasPendingCartUpdates(context.client)) return
      await invalidateCartQueries({
        queryClient: context.client,
        queryKey,
      })
    },
  })
}

export const useMutationEmptyCart = () => {
  const storeParams = useStoreParams()
  const cartInput = useCartGraphQLInput()
  const { state, customerId, retailerData } = cartInput

  return useMutation({
    mutationFn: async () => {
      const response = await fetchGraphql(
        CartEmptyDocument,
        { input: { customerId, retailerData } },
        { authOptional: true },
      )
      if (!response.cartEmpty.success) {
        const error = new Error('GraphQL cart empty failed')
        logError(error)
        throw error
      }
      return response
    },
    onMutate: async (variables: { skipCallbacks: boolean } | void, context) => {
      if (variables?.skipCallbacks) return
      const { queryKey } = cartQueryOptions({ storeParams, state })

      await context.client.cancelQueries({ queryKey })

      const previousCart = context.client.getQueryData(queryKey)

      if (previousCart) {
        context.client.setQueryData(queryKey, createDefaultCartQueryState())
      }
      return { previousCart, queryKey }
    },
    onError: (_error, variables, onMutateResult, context) => {
      if (variables?.skipCallbacks) return
      if (onMutateResult?.previousCart) {
        context.client.setQueryData(onMutateResult.queryKey, onMutateResult.previousCart)
      }
    },
    onSuccess: (_data, variables) => {
      if (variables?.skipCallbacks) return
      refreshCoupons()
    },
    onSettled: (_data, _error, variables, _, context) => {
      if (variables?.skipCallbacks) return
      invalidateCartQueries({
        queryClient: context.client,
        queryKey: cartQueryOptions({ storeParams, state }).queryKey,
      })
    },
  })
}

/**
 * Handles cart conversion when the guest's store_location_id differs from the
 * user's address-derived store_location_id. First converts the cart at
 * the guest store_location, then transfers the converted products to the user's store_location
 */
const convertCartWithTransferProducts = async ({
  guestStore,
  userStoreParams,
  defaultAddress,
  userId,
}: {
  guestStore: GuestStore
  userStoreParams: ReturnType<typeof getStoreParams>
  defaultAddress: Address
  userId: number
}) => {
  const guestStoreParams = {
    ...userStoreParams,
    store_id: guestStore.id,
    metro_id: guestStore.metro_id,
    store_location_id: guestStore.store_location_id,
  }

  await convertCartData(guestStoreParams, defaultAddress, userId)

  try {
    await transferProducts({
      source: {
        store_id: guestStore.id,
        metro_id: guestStore.metro_id,
        store_location_id: guestStore.store_location_id,
      },
      destination: {
        store_id: userStoreParams.store_id!,
        metro_id: userStoreParams.metro_id!,
        store_location_id: userStoreParams.store_location_id!,
      },
    })
    logInfo('cartConversionTransferProductsSuccess', {
      guestStoreLocationId: guestStore.store_location_id,
      userStoreLocationId: userStoreParams.store_location_id,
    })
  } catch (error) {
    logInfo('cartConversionTransferProducts failed - store location mismatch', {
      guest_store_location_id: guestStore.store_location_id,
      user_store_location_id: userStoreParams.store_location_id,
      user_id: userId,
      error,
    })
  }
}

export const useMutationConvertGuestData = () => {
  const { mutateAsync: setupGuestAddressForAccount } = useCompleteGuestUserSignup()
  const { guestStore } = useGuestParams() ?? {}
  const constantGuestStoreRef = useLazyInitializedRef(() => guestStore)

  return useMutation({
    mutationFn: async (_, context) => {
      /*
       * to properly set up a guest user's account, we need to:
       * 1. convert their guest address
       * 2. convert their cart/product notes/coupons using user account data
       * 3. refetch their cart using user account data
       * 4. clear the guest cookie since they're no longer a guest
       */

      // It is rare but possible to run into a race condition where user data
      // is only partially returned from customer broker, so we can instead
      // rely on the PATCH customer response which return data reliably
      const { customer: userResponse, address: defaultAddress } =
        (await setupGuestAddressForAccount()) ?? {}

      if (!userResponse || !defaultAddress) {
        const type = userResponse ? 'address' : 'user'
        const error = new Error(`${type} data was not properly initialized`)
        logError(error)
        throw error
      }

      // it is ok to assert here because we acknowledge that zip is undefined
      // in the cart conversion mutation.
      // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
      const userStoreParams = getStoreParams(userResponse as User, undefined)

      const guestStoreSnapshot = constantGuestStoreRef.current
      const hasStoreLocationMismatch =
        !!guestStoreSnapshot?.store_location_id &&
        guestStoreSnapshot.store_location_id !== userStoreParams.store_location_id &&
        !!userStoreParams.store_id &&
        !!userStoreParams.metro_id &&
        !!userStoreParams.store_location_id

      if (hasStoreLocationMismatch) {
        logInfo(
          'cartConversionTransferProducts: store_location mismatch detected, converting at guest store then transferring',
          {
            guest_store_location_id: guestStoreSnapshot.store_location_id,
            user_store_location_id: userStoreParams.store_location_id,
            user_id: userResponse.id,
          },
        )
        await convertCartWithTransferProducts({
          guestStore: guestStoreSnapshot,
          userStoreParams,
          defaultAddress,
          userId: userResponse.id,
        })
      } else {
        await convertCartData(userStoreParams, defaultAddress, userResponse.id)
      }

      await context.client
        .query(
          cartQueryOptions({
            storeParams: userStoreParams,
            state: defaultAddress.state || '',
          }),
        )
        .catch(noop)
      clearGuestCookies(context.client)
    },
  })
}

/**
 * Merges two carts from the same exact store locations
 */
export const useMutationMergeCart = () => {
  const storeParams = useStoreParams()

  const state = useGlobalDefaultShoppingAddress().state ?? ''
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: mergeCart,
    onSettled(_data, error, variables) {
      datadogRum.addAction('cartMerged', { variables, isSuccess: !error })
    },
    onSuccess: () => {
      queryClient.query({
        ...cartQueryOptions({ storeParams, state }),
        staleTime: 0,
      })
    },
  })
}

/**
 * Transfers a cart from one store location to another for the same retailer (store_id)
 */
export const useMutationTransferCart = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      store_id,
      metro_id,
      store_location_id,
      assistantParams,
    }: Parameters<typeof transferCart>[0]) =>
      transferCart({
        store_id,
        metro_id,
        store_location_id,
        assistantParams,
      }),
    onSettled(_data, error, variables) {
      datadogRum.addAction('cartTransferred', { variables, isSuccess: !error })
    },
    onSuccess: () => {
      // we cannot fetchQuery here because the user store update is async
      // so it may not be up-to-date, so just remove the queries to avoid flash of stale data
      queryClient.removeQueries({ queryKey: [CartQuery] })
      queryClient.removeQueries({ queryKey: [AllCartsPrimaryKey] })
    },
  })
}

/**
 * Transfers products from one retailer cart to another
 */
export const useMutationTransferProducts = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: Parameters<typeof transferProducts>[0]) => transferProducts(params),
    onSettled(_data, error, variables) {
      datadogRum.addAction('productsTransferred', {
        variables,
        isSuccess: !error,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CartQuery] })
    },
  })
}
